import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ReferenceChips from '../references/ReferenceChips'
import Markdown from '../chat/Markdown'
import { openItemInCode } from '../code/openInCode'
import { openGuideInGuides } from '../guides/openInGuides'
import '../references/ReferenceChips.css'

function stamp(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const SOURCE_NAMES = { searchroom: 'Searchroom', global: 'Global AI Search' }

function SharedAnswer({ answer }) {
  const meta = [SOURCE_NAMES[answer.source], answer.model].filter(Boolean).join(' · ')
  return (
    <blockquote className="crm-answer">
      <div className="crm-answer-head">
        <span className="crm-answer-kind">AI answer</span>
        {meta ? <span className="crm-answer-meta">{meta}</span> : null}
      </div>
      {answer.question ? <div className="crm-answer-question">{answer.question}</div> : null}
      <Markdown>{answer.text}</Markdown>
    </blockquote>
  )
}

function MessageCard({
  message,
  reply,
  replies,
  repliesOpen,
  onToggleReplies,
  onReply,
  onLike,
  onEdit,
  onDelete,
  onLikes,
}) {
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(message.text || '')
  const [replyOpen, setReplyOpen] = useState(false)
  const [replyText, setReplyText] = useState('')

  const save = () => {
    const text = draft.trim()
    const attached = (message.items || []).length || (message.guides || []).length || message.answer
    if (!text && (reply || !attached)) return
    onEdit?.(message, text)
    setEditing(false)
  }

  const sendReply = () => {
    const text = replyText.trim()
    if (!text) return
    onReply?.(message, text)
    setReplyText('')
    setReplyOpen(false)
  }

  return (
    <article className={'crm-card' + (message.mine ? ' is-mine' : '') + (reply ? ' is-reply' : '')}>
      <header className="crm-card-head">
        <span className="crm-author">{message.author?.displayName || 'Member'}</span>
        <span className="crm-time">{stamp(message.createdAt)}</span>
      </header>
      {editing ? (
        <div className="crm-edit">
          <textarea className="crm-composer-input" rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} />
          <div className="crm-edit-actions">
            <button type="button" onClick={() => setEditing(false)}>Cancel</button>
            <button type="button" onClick={save}>Save</button>
          </div>
        </div>
      ) : (
        message.text ? <p className="crm-text">{message.text}</p> : null
      )}
      {!reply && message.answer ? <SharedAnswer answer={message.answer} /> : null}
      {!reply && message.items?.length ? (
        <ReferenceChips
          items={message.items}
          label=""
          onOpenItem={(item) => {
            if (openItemInCode(item)) navigate('/code')
          }}
        />
      ) : null}
      {!reply && message.guides?.length ? (
        <div className="crm-guides">
          {message.guides.map((guide) => (
            <button
              key={guide._id}
              type="button"
              className="ref-chip"
              onClick={() => {
                if (openGuideInGuides(guide._id)) navigate('/guides')
              }}
            >
              {guide.title || 'Guide'}
            </button>
          ))}
        </div>
      ) : null}
      <footer className="crm-actions">
        <button
          type="button"
          className={message.liked ? 'is-on' : ''}
          onClick={() => onLike?.(message)}
        >
          {message.liked ? 'Liked' : 'Like'}
        </button>
        {message.likeCount ? (
          <button type="button" onClick={() => onLikes?.(message)}>
            {message.likeCount} {message.likeCount === 1 ? 'like' : 'likes'}
          </button>
        ) : null}
        {!reply ? (
          <button type="button" onClick={() => onToggleReplies?.(message)}>
            {message.replyCount || 0} {message.replyCount === 1 ? 'reply' : 'replies'}
          </button>
        ) : null}
        {!reply ? (
          <button type="button" onClick={() => setReplyOpen((open) => !open)}>
            Reply
          </button>
        ) : null}
        {message.mine ? (
          <>
            <button type="button" onClick={() => { setDraft(message.text || ''); setEditing(true) }}>
              Edit
            </button>
            <button type="button" className="is-danger" onClick={() => onDelete?.(message)}>
              Delete
            </button>
          </>
        ) : null}
      </footer>
      {!reply && replyOpen ? (
        <div className="crm-reply-box">
          <textarea
            className="crm-composer-input"
            rows={2}
            value={replyText}
            placeholder="Write a reply…"
            onChange={(event) => setReplyText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                sendReply()
              }
            }}
          />
          <button type="button" className="crm-send" onClick={sendReply}>Send</button>
        </div>
      ) : null}
      {!reply && repliesOpen ? (
        <div className="crm-replies">
          {replies === 'loading' ? <div className="crm-status">Loading replies…</div> : null}
          {Array.isArray(replies)
            ? replies.map((entry) => (
                <MessageCard
                  key={entry._id}
                  message={entry}
                  reply
                  onLike={onLike}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onLikes={onLikes}
                />
              ))
            : null}
        </div>
      ) : null}
    </article>
  )
}

export default MessageCard
